/**
 * MarkdownEditor — small markdown editor with a formatting toolbar and a
 * live preview pane. Built for the Legal Pages CMS (Privacy / Terms) but
 * reusable wherever an admin needs to write rich prose.
 *
 * The output is plain Markdown — rendered by `react-markdown` on the
 * public-facing page. This avoids the dependency / bundle cost of a true
 * WYSIWYG library (TipTap/Slate/Quill) while still giving admins one-tap
 * Bold, Italic, H2, H3, Link, List, Quote, HR buttons.
 *
 * Props:
 *   value     string         — current markdown text
 *   onChange  fn(next)       — called with the new markdown text
 *   minHeight string         — tailwind h-* class (default "h-96")
 *   testid    string         — prefix for data-testids
 */
import { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "./ui/button";
import {
  TextB, TextItalic, TextHOne, TextHTwo, ListBullets, ListNumbers,
  Quotes, Link as LinkIcon, Minus, Eye, EyeSlash,
} from "@phosphor-icons/react";

export default function MarkdownEditor({ value, onChange, minHeight = "h-96", testid = "md-editor" }) {
  const ref = useRef(null);
  const [showPreview, setShowPreview] = useState(true);

  // Wrap the current selection (or "text" placeholder) with `prefix` and `suffix`.
  // If `multiline` is true, the wrap is applied to the start of each non-empty line.
  const wrap = (prefix, suffix = "", multiline = false) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart, end = el.selectionEnd;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);
    const placeholder = sel || "text";
    let nextSel;
    if (multiline) {
      nextSel = placeholder.split("\n").map((line) => line.trim() ? `${prefix}${line}` : line).join("\n");
    } else {
      nextSel = `${prefix}${placeholder}${suffix}`;
    }
    const next = `${before}${nextSel}${after}`;
    onChange(next);
    // Restore caret after the inserted text.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + nextSel.length, start + nextSel.length);
    });
  };

  const insertBlock = (block) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const before = value.slice(0, start);
    const after = value.slice(start);
    const sep1 = before && !before.endsWith("\n\n") ? (before.endsWith("\n") ? "\n" : "\n\n") : "";
    const sep2 = after && !after.startsWith("\n\n") ? (after.startsWith("\n") ? "\n" : "\n\n") : "";
    const next = `${before}${sep1}${block}${sep2}${after}`;
    onChange(next);
    requestAnimationFrame(() => el.focus());
  };

  const insertLink = () => {
    const url = window.prompt("Link URL:", "https://");
    if (!url) return;
    wrap("[", `](${url})`);
  };

  const Tool = ({ onClick, title, children, testid: t }) => (
    <button type="button" title={title} onClick={onClick} data-testid={`${testid}-${t}`}
      className="px-2 py-1.5 border border-neutral-300 hover:border-black hover:bg-black hover:text-white text-sm rounded-none transition-colors">
      {children}
    </button>
  );

  return (
    <div className="border-2 border-neutral-200" data-testid={testid}>
      <div className="border-b border-neutral-200 bg-neutral-50 p-2 flex flex-wrap gap-1 items-center">
        <Tool onClick={() => wrap("**", "**")} title="Bold (Ctrl+B)" testid="bold"><TextB size={14} weight="bold" /></Tool>
        <Tool onClick={() => wrap("*", "*")} title="Italic" testid="italic"><TextItalic size={14} weight="bold" /></Tool>
        <div className="w-px h-5 bg-neutral-300 mx-1" />
        <Tool onClick={() => insertBlock("## Section heading")} title="Heading H2" testid="h2"><TextHOne size={14} weight="bold" /></Tool>
        <Tool onClick={() => insertBlock("### Sub-heading")} title="Heading H3" testid="h3"><TextHTwo size={14} weight="bold" /></Tool>
        <div className="w-px h-5 bg-neutral-300 mx-1" />
        <Tool onClick={() => wrap("- ", "", true)} title="Bulleted list" testid="ul"><ListBullets size={14} weight="bold" /></Tool>
        <Tool onClick={() => wrap("1. ", "", true)} title="Numbered list" testid="ol"><ListNumbers size={14} weight="bold" /></Tool>
        <Tool onClick={() => wrap("> ", "", true)} title="Quote" testid="quote"><Quotes size={14} weight="bold" /></Tool>
        <Tool onClick={insertLink} title="Insert link" testid="link"><LinkIcon size={14} weight="bold" /></Tool>
        <Tool onClick={() => insertBlock("---")} title="Horizontal rule" testid="hr"><Minus size={14} weight="bold" /></Tool>
        <div className="ml-auto">
          <Button type="button" variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)} className="rounded-none text-xs" data-testid={`${testid}-toggle-preview`}>
            {showPreview ? <><EyeSlash size={12} className="mr-1" /> Hide preview</> : <><Eye size={12} className="mr-1" /> Show preview</>}
          </Button>
        </div>
      </div>
      <div className={`grid ${showPreview ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"} divide-x divide-neutral-200`}>
        <textarea
          ref={ref}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") { e.preventDefault(); wrap("**", "**"); }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "i") { e.preventDefault(); wrap("*", "*"); }
          }}
          className={`${minHeight} w-full px-4 py-3 font-mono text-sm leading-relaxed outline-none resize-none`}
          placeholder="Start writing… Use the toolbar above for formatting."
          data-testid={`${testid}-textarea`}
          spellCheck="true"
        />
        {showPreview && (
          <div className={`${minHeight} overflow-y-auto px-5 py-3 bg-white`} data-testid={`${testid}-preview`}>
            {value ? (
              <article className="prose prose-neutral prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
              </article>
            ) : (
              <div className="text-neutral-400 italic text-sm">Live preview will appear here as you type…</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
